import React, { ChangeEvent, useCallback } from "react";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import { Dialog, IconButton, Paper, PaperProps, Select, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { animated, config, useSpring } from "@react-spring/web";
import type { SidebarLinkMeetingSettings } from "@ui/scheduling-links-sidebar/scheduling-links-sidebar";

import {
  TimeZonePicker,
  TimeZoneDisplayMode,
} from "@clockwise/web-commons/src/ui/time-zone-picker";
import { narrowMediaQuery } from "@ui/scheduling-link/scheduling-link.styles";
import { Close, Link, Settings } from "@material-ui/icons";
import { CopyTimesPreview, formatDurationCopyTimes } from "./CopyTimesPreview";
import { CopyText } from "@ui/copy-text/CopyText";
import { CwLink } from "@ui/cw-link";
import { BestTimes_viewer_schedulingLinkDetails_SchedulingLinkDetails_suggestedSlotsByDate_suggestedSlots as SuggestedSlot } from "./__generated__/BestTimes";
import { TrackingEvents, useTrackOnce } from "@util/analytics.util";

const useStyles = makeStyles({
  paper: {
    padding: "26px 34px 30px",
    minWidth: 320,
    maxWidth: 550,
    boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.25)",
    borderRadius: 8,

    [narrowMediaQuery]: {
      height: "100%",
    },
  },
});

const AnimatedPaper = animated(Paper);

export interface ICopyBestTimesModalProps {
  dialogOpen: boolean;
  fullscreen: boolean;
  onClose: () => void;
  onCopy: (text: string) => void;
  linkUrl: string;
  meetingSettings: Pick<SidebarLinkMeetingSettings, "timingSettings" | "name" | "slug">;
  bestTimeSlots: SuggestedSlot[];
  loading: boolean;
  errorMsg: string | null;
  numDaysToShow: number;
  setNumDaysToShow: (numDaysToShow: number) => void;
  targetTimeZone: string;
  setTargetTimezone: (targetTimeZone: string) => void;
}

export const CopyBestTimesModal = ({
  dialogOpen = true,
  fullscreen = false,
  onClose,
  onCopy,
  linkUrl,
  meetingSettings,
  bestTimeSlots,
  loading,
  errorMsg,
  numDaysToShow,
  setNumDaysToShow,
  targetTimeZone,
  setTargetTimezone,
}: ICopyBestTimesModalProps) => {
  const classes = useStyles();
  const trackCopyLink = useTrackOnce(TrackingEvents.LINKS.COPY_TIMES.COPY_LINK);

  const modalStyles = useSpring({
    from: {
      scale: 0.83,
      opacity: 0,
    },
    to: {
      scale: 1,
      opacity: 1,
    },
    config: config.stiff,
  });

  const duration = meetingSettings.timingSettings.durationMinutes;
  const windowSizeDays = meetingSettings.timingSettings.timeRange.windowSizeDays;
  const shortTzString = DateTime.now().setZone(targetTimeZone).offsetNameShort;
  const [customizing, setCustomizing] = React.useState(false);

  const handleCopyLink = useCallback(
    (link: string) => {
      onCopy(link);
      trackCopyLink();
    },
    [trackCopyLink, onCopy],
  );

  return (
    <Dialog
      open={dialogOpen}
      onClose={onClose}
      fullScreen={fullscreen}
      disableRestoreFocus
      disableScrollLock
      PaperComponent={AnimatedPaper as React.ComponentType<PaperProps>}
      PaperProps={{
        style: {
          // PaperProps wants CSS properties so must cast the animated values to number
          scale: (modalStyles.scale as unknown) as number,
          opacity: (modalStyles.opacity as unknown) as number,
          visibility: dialogOpen ? "visible" : "hidden",
        },
        className: classes.paper,
      }}
      BackdropProps={{ style: { backgroundColor: "unset" } }}
      cw-id="link-copy-best-times-modal"
    >
      <div className="cw-flex cw-flex-col cw-gap-8">
        <div className="cw-flex cw-justify-between cw-items-center -cw-mb-4">
          <h2 className="cw-font-brand cw-text-20 cw-text-dusk-standard cw-font-bold cw-m-0">
            Share your availability
          </h2>
          <IconButton onClick={onClose} className="-cw-mr-3" cw-id="close-copy-best-times-modal">
            <Close />
          </IconButton>
        </div>
        <div>
          <div className="cw-text-14 cw-text-standard-grey cw-font-body cw-mb-2 cw-leading-[1.15rem]">
            This link will allow recipients to book a
            <strong className="cw-font-bold"> {formatDurationCopyTimes(duration)}</strong> meeting
            with you in the next
            <strong className="cw-font-bold"> {windowSizeDays} days</strong> using your
            <strong className="cw-font-bold"> {meetingSettings.name}</strong> scheduling link
          </div>
          <CopyText
            copyAriaLabel="copy scheduling link url"
            onCopy={() => handleCopyLink(linkUrl)}
            startIcon={<Link />}
            text={linkUrl}
            urlFormatting
          >
            {linkUrl}
          </CopyText>
        </div>
        <div>
          <div className="cw-text-14 cw-text-standard-grey cw-font-body cw-mb-2 cw-leading-[1.15rem]">
            Or, paste this text into an email or Slack message to offer a few suggested times along
            with the link to <strong className="cw-font-bold">{meetingSettings.name}</strong>
          </div>
          <CopyTimesPreview
            durationMinutes={duration}
            linkUrl={linkUrl}
            slots={bestTimeSlots}
            targetTimeZone={targetTimeZone}
            loading={loading}
            slug={meetingSettings.slug}
            onCopyRichText={onCopy}
            errorMsg={errorMsg}
            numDaysToShow={numDaysToShow}
          />
          {!!bestTimeSlots.length && !customizing && (
            <div className="cw-inline-flex cw-items-center cw-text-14 cw-text-standard-grey cw-font-body cw-leading-[1.15rem] cw-flex-wrap cw-mt-4">
              <Settings className="cw-text-slate-standard cw-text-16 cw-mr-[5px]" />

              <span className="cw-mr-[6px]" cw-id="copy-times-customization-text">
                {" "}
                Showing {pluralize("day", numDaysToShow, true)} of availability, in your time zone (
                {shortTzString})
              </span>
              <CwLink onClick={() => setCustomizing(true)} size={14} className="ml-0.5">
                Customize
              </CwLink>
            </div>
          )}
          {customizing && (
            <div className="cw-flex cw-flex-col cw-gap-3 cw-items-start cw-text-14 cw-text-standard-grey cw-font-body cw-leading-[1.15rem] cw-mt-4">
              <div className="cw-inline-flex cw-items-center">
                <span>Show up to</span>
                <Select
                  value={numDaysToShow}
                  disableUnderline
                  variant="filled"
                  inputProps={{
                    className: "cw-pt-[11px] cw-pr-[32px] cw-pb-[9px] cw-pl-[10px]",
                  }}
                  className="cw-mx-2 cw-rounded"
                  onChange={({ target: { value: newNumDays } }: ChangeEvent<{ value: number }>) =>
                    setNumDaysToShow(newNumDays)
                  }
                >
                  {[1, 2, 3, 4, 5].map((d) => (
                    <MenuItem key={d} value={d}>
                      {pluralize("day", d, true)}
                    </MenuItem>
                  ))}
                </Select>
                <span>of availability in </span>
              </div>
              <TimeZonePicker
                className="cw-flex-grow"
                value={targetTimeZone}
                onChange={setTargetTimezone}
                timeZoneDisplayMode={TimeZoneDisplayMode.JustPicker}
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
